import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon } from 'lucide-react';
import { ProductCard } from '../shared/ProductCard';
import { ResultTimeline } from './ResultTimeline';
import { useCart, useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { NotFound } from '../layout/NotFound';
import { isSoldOut } from '../../core/rules/stock';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { euro } from '../../core/format/currency';
import { ProductGallery } from './ProductGallery';
import { ProductBuyBox } from './ProductBuyBox';
import { ProductDetails } from './ProductDetails';
import { ProductPairs } from './ProductPairs';
import { ProductReviews } from './ProductReviews';
import { ADDED_FEEDBACK_MS, giftHintFor, relatedProducts } from './productRules';
import { FloatingBar } from '../layout/FloatingBar';
import { btnPrimary, btnPriceAmount, btnPriceBar, btnPriceBarQuiet, btnPriceLabel } from '../../ui/styles';
import { closingHeading } from '../../ui/sections';

/** De productpagina: foto's, koopblok, resultaat in de tijd, "samen met", reviews en meer voor dit huidtype. */
export function Product() {
  const {
    id
  } = useParams();
  const {
    products,
    productById,
    productsByIds,
    reviewsFor,
    routinesWithProduct,
    treatmentForRoutine
  } = useCatalog();
  const found = id ? productById(id) : undefined;
  // Cadeaubonnen en kuren hebben geen eigen productpagina (een kuur staat bij de behandeling).
  const product = found?.kind === 'giftcard' || found?.kind === 'kuur' ? undefined : found;
  const {
    add
  } = useCartActions();
  const {
    openCart
  } = useOverlayActions();
  const {
    giftSubtotal,
    nextGift
  } = useCart();
  const navigate = useNavigate();
  // Kwam je uit de collectie (ProductCard geeft dat mee), dan brengt "← Webshop" je terug naar
  // dezelfde filters én scrollpositie: één stap terug in de geschiedenis in plaats van een nieuwe pagina.
  const {
    state
  } = useLocation() as {
    state: {
      fromShop?: string;
    } | null;
  };
  const fromShop = state?.fromShop?.startsWith('/webshop') ? state.fromShop : null;
  const [subscribe, setSubscribe] = useState(false);
  const [added, setAdded] = useState(false);
  useEffect(() => {
    setSubscribe(false);
    setAdded(false);
  }, [id]);

  // "Toegevoegd" na even terug — en de timer opruimen als je eerder wegklikt.
  useEffect(() => {
    if (!added) return;
    const t = window.setTimeout(() => setAdded(false), ADDED_FEEDBACK_MS);
    return () => window.clearTimeout(t);
  }, [added]);
  const partners = useMemo(() => product ? productsByIds(product.pairs) : [], [product, productsByIds]);
  const related = useMemo(() => product ? relatedProducts(product, products) : [], [product, products]);
  if (!product) {
    return <NotFound title="Dit product vinden we niet" body="Misschien is het uitverkocht of heeft het een nieuwe naam. In de webshop vind je de hele collectie." backTo={{
      to: '/webshop',
      label: 'Terug naar de webshop'
    }} data-id="element-7995" data-mp="6xxx368vm1ao" />;
  }

  // Voorraad 0 = uitverkocht (onbekend = leverbaar). Zelfde regel als de "availability" in de structured data.
  const soldOut = isSoldOut(product);
  const inRoutines = routinesWithProduct(product.id);
  const salonTreatment = inRoutines.map(treatmentForRoutine).find(Boolean);

  // Alleen "Toegevoegd" als het ook echt lukte (bijv. niet als de voorraad al in de mand zit).
  const handleAdd = () => {
    if (add(product, {
      subscribe
    })) setAdded(true);
  };
  // Vanuit de koopbalk zegt de balk zelf al "Toegevoegd · Bekijk winkelmand": geen tweede melding erbovenop.
  const handleBarAdd = () => {
    if (add(product, {
      subscribe,
      silent: true
    })) setAdded(true);
  };
  return <>
      <div className="mx-auto max-w-content px-6 pt-6 lg:px-10" data-id="element-7996" data-mp="1ilq7rxjpqbmn">
        {/* Kruimelpad: het merk is een filter in de webshop, de huidige pagina is het product zelf. */}
        <nav aria-label="Kruimelpad" className="flex min-w-0 items-center gap-2 text-[14px] text-muted" data-id="element-7997" data-mp="3dnk7x3lkrosd">
          <Link to={fromShop ?? '/webshop'} onClick={e => {
          if (!fromShop) return;
          e.preventDefault();
          navigate(-1);
        }} className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7998" data-mp="3ildbdovhu4oy">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7999" data-mp="2zvdsxku5rnui" />
            Webshop
          </Link>
          <span aria-hidden="true" data-id="element-8000" data-mp="2kz64277t1y8w">/</span>
          <Link to={`/webshop?merk=${encodeURIComponent(product.brand)}`} className="shrink-0 transition-colors duration-150 ease-soft hover:text-ink" data-id="element-8001" data-mp="2l0hc4j8a9s5t">
            {product.brand}
          </Link>
          <span aria-hidden="true" className="hidden sm:inline" data-id="element-8002" data-mp="j4x6a88ujrv9">
            /
          </span>
          <span aria-current="page" className="hidden truncate text-ink sm:inline" data-id="element-8003" data-mp="o5bcrlusutr6">
            {product.name}
          </span>
        </nav>
      </div>

      <section className="mx-auto grid max-w-content gap-10 px-6 pb-20 pt-6 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-28" data-id="element-8004" data-mp="23ptvtx5hxrdl">
        {/* Foto's: packshot, extra beelden uit het beheer, en de salonfoto. */}
        <div className="min-w-0 lg:col-span-7" data-id="element-8005" data-mp="2dmyh7uripitc">
          <ProductGallery product={product} salonTreatment={salonTreatment} data-id="element-8006" data-mp="2dl5cil0gt8nz" />
        </div>

        <div className="lg:col-span-5" data-id="element-8007" data-mp="2rgpv1fca7ouv">
          <ProductBuyBox product={product} soldOut={soldOut} subscribe={subscribe} onSubscribe={setSubscribe} added={added} onAdd={handleAdd} onViewCart={openCart} giftHint={giftHintFor(product.price, giftSubtotal, nextGift)} data-id="element-8008" data-mp="2ndksjcs7bn68">
            <ProductDetails product={product} inRoutines={inRoutines} data-id="element-8009" data-mp="16zkz8knv2vry" />
          </ProductBuyBox>
        </div>
      </section>

      <ResultTimeline product={product} data-id="element-8010" data-mp="121t7bqn7v9nr" />

      {partners.length > 0 && <ProductPairs product={product} partners={partners} data-id="element-8011" data-mp="3239gsv1sdix0" />}

      <ProductReviews product={product} reviews={reviewsFor(product.id)} data-id="element-8012" data-mp="370retqe9mdq8" />

      {related.length > 0 && <section className="bg-sand py-20 lg:py-28" aria-labelledby="related-title" data-id="element-8013" data-mp="3b24ne88qsv0u">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8014" data-mp="3g1hb0v7fu64h">
            <h2 id="related-title" className={`${closingHeading} text-ink`} data-id="element-8015" data-mp="3g30o6cxss121">
              Ook voor jouw huidtype
            </h2>
            <ul className="mt-10 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 lg:grid-cols-4" data-id="element-8016" data-mp="3pznaz79op26p">
              {related.map(p => <li key={p.id} className="h-full" data-id="element-8017" data-mp="3urojci3qe8rg">
                  <ProductCard product={p} data-id="element-8018" data-mp="3uszoomctroko" />
                </li>)}
            </ul>
          </div>
        </section>}

      {/* Mobiele koopbalk: dezelfde zwevende balk als de actiebalk, altijd in beeld op de productpagina. */}
      <FloatingBar show data-id="element-8019" data-mp="cm21rw6vw0ke">
        {soldOut ?
      // Uitverkocht: de balk biedt de enige zinvolle volgende stap — vragen om een alternatief.
      <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnPriceBarQuiet} data-id="element-8020" data-mp="mmky42tvio3s">
            <span data-id="element-8021" data-mp="hs74o5yrur1r">Uitverkocht · vraag {founder.firstName}</span>
            <span className="tabular-nums text-muted" data-id="element-8022" data-mp="cr9q03cz0f50">{euro(product.price)}</span>
            <span className="sr-only" data-id="element-8023" data-mp="8q7otgzhtt0e">{shopLabels.viaWhatsApp}</span>
          </a> : added ?
      // Net toegevoegd: zeg het, en bied de logische volgende stap aan in plaats van nog een keer toevoegen.
      <div className="flex items-center gap-2" data-id="element-8024" data-mp="10gvo0nhlwobf">
            <p className="flex min-w-0 flex-1 items-center gap-2 pl-4 text-[14px] text-ink" data-id="element-8025" data-mp="vpdl3tw0wdnt">
              <CheckIcon className="h-4 w-4 shrink-0 text-sage" strokeWidth={1.75} aria-hidden="true" data-id="element-8026" data-mp="roa6tbtmipop" />
              <span className="truncate" data-id="element-8027" data-mp="1o5og28v3yc0r">Toegevoegd</span>
            </p>
            <button type="button" onClick={openCart} aria-haspopup="dialog" className={`${btnPrimary} min-h-[48px] shrink-0`} data-id="element-8028" data-mp="1o7rdvf2i59g1">
              Bekijk winkelmand
            </button>
          </div> : <button type="button" onClick={handleBarAdd} aria-label={shopLabels.addToCart(product)} className={btnPriceBar} data-id="element-8029" data-mp="27tynumaxmu6o">
            <span className={btnPriceLabel} data-id="element-8030" data-mp="1t5klsdlcb9r6">In winkelmand</span>
            <span className={btnPriceAmount} data-id="element-8031" data-mp="1t37w1e9cbv3k">{euro(product.price)}</span>
          </button>}
      </FloatingBar>
      <span className="sr-only" role="status" data-id="element-8032" data-mp="2ltzpw52xo7nq">
        {added ? `${product.name} ${shopLabels.addedToCart}` : ''}
      </span>
    </>;
}